import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { setInStorage } from '../features/storageSlice';

import { FilterStorageVersion, getInitialSearch } from '../filtering';

import fastStorage from '../fastStorage';

import { SearchModelBase } from '../fms.data/models/SearchModelBase';
import { addMonths, nowDate } from '../dateUtils';
import _ from 'lodash';

/**
 * Hook for setting screen filters from other screens.
 * For example: MBL list screen wants to set RefNo filter for HBL list screen before
 * redirecting to show only related HBLs.
 *
 * @export
 * @return {*} 
 */
export default function useExternalScreenFilter() {
  const history = useHistory();

  const dispatch = useDispatch();

  function getInitialSearchWithPageSizeFromSettings() {
    return {
      ...getInitialSearch()
    }
  }

  /**
   * setFilterForScreenExternally
   *
   * @param {string} url - url of screen to set filter for
   * @param {string} columnId - columnId (accessor in data object) filter is for
   * @param {string} columnLabel - label of column
   * @param {string} text - filter text
   * @param {*} orderBy - order by accessor
   * @param {boolean} orderByAscending - true for ascending, false for descending ordering
   */
  function setFilterForScreenExternally(url:string, columnId:string, columnLabel:string, text:string|number, orderBy:any, orderByAscending:boolean, startWithEntry:boolean = true,
    searchModel:SearchModelBase|null = null, columnIdEx:string|null = null, textEx:string|number|null = null, isExpressSearch = false, switchList = false) {
    const otherStorageKey = `filter-${FilterStorageVersion}-${url}`;
    const otherFilterOrdersStorageKey = `filter-order-${FilterStorageVersion}-${url}`;
    const otherStorageExternalSetKey = `filter-external-set-${FilterStorageVersion}-${url}`;
    const otherStorageExternalSetSourceKey = `filter-external-set-source-${FilterStorageVersion}-${url}`;
    const todayBefore3Month = addMonths(nowDate(), -3);
    const dataStorageKey = `ifms-list-data-${url}`;

    const filterObj = {
      ...getInitialSearchWithPageSizeFromSettings(),
      ...searchModel,   //<pt220904>
      ...{
        general1: columnId,
        generalText1: text?.toString(),
        searchConditions: [{ fieldName: columnId, condition: 2 }],
        orderBy,
        orderByAscending,
        dateFrom: isExpressSearch ? todayBefore3Month : null,
        date: isExpressSearch ? searchModel?.expressSearchCol??'' : ''
      },
    };
    const filterOrdersObj = isExpressSearch ? 
    [{
      columnId: searchModel?.expressSearchCol, slot: 'date', type: 'date', label: 'Create', value: `${todayBefore3Month} ~`, switchList: switchList
    }, 
    {
      // columnId, slot: 'general1', type: 'general', label: columnLabel, value: text
      columnId: (columnIdEx && textEx !== null ? columnIdEx : columnId), slot: 'general1', type: 'general', label: columnLabel, value: (textEx !== null ? textEx : text), switchList: switchList
    }]
    :[{
      // columnId, slot: 'general1', type: 'general', label: columnLabel, value: text
      columnId: (columnIdEx && textEx !== null ? columnIdEx : columnId), slot: 'general1', type: 'general', label: columnLabel, value: (textEx !== null ? textEx : text), switchList: switchList
    }];

    
    if (url.includes('/common/joblistview/RPCSP/shipmentStatus')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('shipStatus-cust-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }
      
      const keyMatch3 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-tracking-ListFilter'));
      if (keyMatch3) {
        delete fastStorage[keyMatch3];
      }

      const keyMatch4 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-blType-ListFilter'));
      if (keyMatch4) {
        delete fastStorage[keyMatch4];
      }
    }

    if(url.includes('/common/joblistview/AICTR/aiCenter')){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('aiCenter-result-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }


    const currentUrl = `${history.location.pathname}${history.location.search}`;
    
    const obj:any = {};
    obj[otherStorageKey] = filterObj;
    obj[otherStorageExternalSetKey] = filterObj;
    obj[otherStorageExternalSetSourceKey] = currentUrl;
    obj[otherFilterOrdersStorageKey] = filterOrdersObj;

    // Delete the data for the screen (if exists) to make sure next time screen is opened, this new filter is used
    delete fastStorage[dataStorageKey];
    delete fastStorage[`${otherStorageKey}-order-initialized`];
    // <240827.dwshin 불필요하게 List 2번 fatch 하도록 되는 상황이 발생해서 우선 comment out.
    // // 이전에 적용된 필터를 삭제
    // dispatch(removeFromStorage(otherStorageKey));
    // dispatch(removeFromStorage(otherStorageExternalSetKey));
    // dispatch(removeFromStorage(otherStorageExternalSetSourceKey));
    // dispatch(removeFromStorage(otherFilterOrdersStorageKey));
    // 240827.dwshin>
    
    // 새로운 필터 옵션 적용
    dispatch(setInStorage(obj));
  }

  function setFilterForSearchModel(url:string, searchModel:SearchModelBase|null = null, filterOrdersObj:any) {
    const otherStorageKey = `filter-${FilterStorageVersion}-${url}`;
    const otherFilterOrdersStorageKey = `filter-order-${FilterStorageVersion}-${url}`;
    const otherStorageExternalSetKey = `filter-external-set-${FilterStorageVersion}-${url}`;
    const otherStorageExternalSetSourceKey = `filter-external-set-source-${FilterStorageVersion}-${url}`;
    const dataStorageKey = `ifms-list-data-${url}`;

    if (searchModel) {
      setGeneralSearchConditions(searchModel);
    }

    let initialFilter = getInitialSearchWithPageSizeFromSettings();
    let filterObj = {
      ...initialFilter,
      ..._.pick(searchModel, Object.keys(initialFilter))
    };

    const currentUrl = `${history.location.pathname}${history.location.search}`;

    const obj:any = {};
    obj[otherStorageKey] = filterObj;
    obj[otherStorageExternalSetKey] = filterObj;
    obj[otherStorageExternalSetSourceKey] = currentUrl;
    obj[otherFilterOrdersStorageKey] = filterOrdersObj;

    // Delete the data for the screen (if exists) to make sure next time screen is opened, this new filter is used
    delete fastStorage[dataStorageKey];

    // <240827.dwshin 불필요하게 List 2번 fatch 하도록 되는 상황이 발생해서 우선 comment out.
    // // 이전에 적용된 필터를 삭제
    // dispatch(removeFromStorage(otherStorageKey));
    // dispatch(removeFromStorage(otherStorageExternalSetKey));
    // dispatch(removeFromStorage(otherStorageExternalSetSourceKey));
    // dispatch(removeFromStorage(otherFilterOrdersStorageKey));
    // 240827.dwshin>
    
    // 새로운 필터 옵션 적용
    dispatch(setInStorage(obj));
  }

  return {setFilterForScreenExternally, setFilterForSearchModel};
}

function setGeneralSearchConditions(searchModel: any) {
  if (!searchModel) return;

  if (!searchModel.searchConditions) {
    searchModel.searchConditions = [];
  }

  const existingFields = new Set(
    searchModel.searchConditions.map((x: any) => x.fieldName)
  );

  const generalSlots = ['general1', 'general2', 'general3'];

  generalSlots.forEach(slot => {
    const fieldName = searchModel[slot];
    if (!fieldName) return;

    if (!existingFields.has(fieldName)) {
      searchModel.searchConditions.push({
        fieldName,
        condition: 2
      });
    }
  });
}